import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { fetchMetadataRevisions, Revision } from '@api/editorialLayout/revisions';
import useLoad from '@hooks/useLoad';

import { RevisionId, RevisionMap } from '../../types';

import RawDataRevisionContext, { RawDataRevisionValues } from './context';

type RawDataRevisionProviderProps = PropsWithChildren<{
  playbackOptionId: string;
  sourceId?: string;
}>;

const RawDataRevisionProvider: FC<RawDataRevisionProviderProps> = ({
  children,
  playbackOptionId,
  sourceId,
}) => {
  const [highlightedHash, setHighlightedHash] = useState<string>();
  const [revisions, setRevisions] = useState<Revision[]>();
  const [revisionsToCompare, setRevisionsToCompare] = useState<RevisionMap>(new Map());

  const toggleRevisionToCompare = (revisionId: RevisionId) =>
    setRevisionsToCompare(prev => {
      const { hash } = revisionId;
      const tmp = new Map(prev);
      if (tmp.has(hash)) tmp.delete(hash);
      else tmp.set(hash, revisionId);
      return tmp;
    });

  const canAddRevisionToCompare = revisionsToCompare.size < 2;

  const isMuted = (hash: string) => !!(highlightedHash && hash !== highlightedHash);

  const before = revisions && revisions.at(-1) && revisions.at(-1)!.timestamp;
  const hasMore = !revisions || (revisions.at(-1) && revisions.at(-1)!.revision > 1);

  const queryResult = useLoad(
    () => fetchMetadataRevisions(playbackOptionId, before),
    ['raw-data-revisions', playbackOptionId, before],
    { enabled: hasMore }
  );

  const { data, isLoading } = queryResult;

  useEffect(() => {
    if (data) setRevisions(prev => (prev ? [...prev, ...data] : data));
  }, [data]);

  const groupedRevisions = useMemo(() => {
    if (!isLoading && !hasMore)
      return revisions.reduce(
        (acc: Record<string, Revision[]>, cur) => ({
          ...acc,
          [cur.hash]: [...(cur.hash in acc ? acc[cur.hash] : []), cur],
        }),
        {}
      );
  }, [isLoading, hasMore, revisions]);

  const hasRepeatedMetadata = useMemo(
    () =>
      !!groupedRevisions &&
      Object.entries(groupedRevisions).some(revisionGroup => revisionGroup.length > 1),
    [groupedRevisions]
  );

  const getRevisionGroup = (hash: string) => groupedRevisions?.[hash];

  const values: RawDataRevisionValues = {
    canAddRevisionToCompare,
    getRevisionGroup,
    groupedRevisions,
    hasRepeatedMetadata,
    highlightedHash,
    isLoading,
    isMuted,
    queryResult,
    revisions,
    revisionsToCompare,
    setHighlightedHash,
    sourceId,
    toggleRevisionToCompare,
  };

  return (
    <RawDataRevisionContext.Provider value={values}>{children}</RawDataRevisionContext.Provider>
  );
};

export default RawDataRevisionProvider;
